import { Helmet } from "react-helmet-async";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import SafeImage from "../Utils/ImageHelper";
import api from "../Utils/Axios";

// Mapping for item unit codes to Urdu labels
const itemUnitLabels = {
  units: { en: 'Units', ur: 'یونٹس' },
  crates: { en: 'Crates', ur: 'کریٹس' },
  bags: { en: 'Bags', ur: 'بوریاں' },
  pali: { en: 'Pali', ur: 'پلی' },
  paiti: { en: 'Paiti', ur: 'پیتی' },
  // Case variations
  'Pali': { en: 'Pali', ur: 'پلی' },
  'PALI': { en: 'Pali', ur: 'پلی' },
  'Paiti': { en: 'Paiti', ur: 'پیتی' },
  'PAITI': { en: 'Paiti', ur: 'پیتی' },
};

function ReceiptView({ receiptData, multipleReceiptsData = [], onClose, isInline = false, companyDataProp = null }) {
  const [language, setLanguage] = useState(() => localStorage.getItem("companyLanguage") || 'ur');
  const [companyData, setCompanyData] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [printMode, setPrintMode] = useState('half-a4'); // 'half-a4' (2 receipts) or 'full-a4' (4 receipts)
  const [printingMode, setPrintingMode] = useState(null); // Track which button is currently printing
  const receiptRef = useRef(null);

  // Memoize receipts to display
  const receiptsToDisplay = useMemo(() => {
    return multipleReceiptsData.length > 0 ? multipleReceiptsData : (receiptData ? [receiptData] : []);
  }, [multipleReceiptsData, receiptData]);

  // Memoize receipt pages grouping based on print mode
  const receiptPages = useMemo(() => {
    const receiptsPerPage = printMode === 'full-a4' ? 4 : 2;
    const pages = [];
    for (let i = 0; i < receiptsToDisplay.length; i += receiptsPerPage) {
      pages.push(receiptsToDisplay.slice(i, i + receiptsPerPage));
    }
    return pages;
  }, [receiptsToDisplay, printMode]);

  // Use prop if provided, otherwise fetch (only if not inline)
  useEffect(() => {
    if (companyDataProp) {
      setCompanyData(companyDataProp);
      return;
    }

    // If we are inline, we expect company data to be passed via props
    // Don't auto-fetch to avoid redundant requests and timeouts
    if (isInline) return;

    const fetchCompany = async () => {
      // Only fetch if not already loaded
      if (companyData) return;

      try {
        const token = localStorage.getItem("companyToken");
        if (token) {
          const response = await api.get("/company/profile", {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCompanyData(response.data.company);
        }
      } catch (error) {
        console.error("Failed to fetch company:", error);
      }
    };

    fetchCompany();
  }, [companyDataProp, isInline]); // Memoization safety

  const handleDownloadPDF = useCallback(async () => {
    if (!receiptRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      const container = receiptRef.current;
      // Reduce scale for better performance
      const canvas = await html2canvas(container, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png', 0.9);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const firstReceipt = receiptsToDisplay[0];
      const partyName = firstReceipt?.party?.name || 'Receipt';
      const fileName = receiptsToDisplay.length > 1
        ? `Receipts-${receiptsToDisplay.length}-${Date.now()}.pdf`
        : `Receipt-${partyName}-${Date.now()}.pdf`;

      pdf.save(fileName);
      toast.success(language === 'ur' ? 'PDF ڈاؤن لوڈ ہو گیا' : 'PDF downloaded successfully');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(language === 'ur' ? 'PDF ڈاؤن لوڈ میں ناکام' : 'Failed to download PDF');
    } finally {
      setIsDownloading(false);
    }
  }, [receiptsToDisplay, language, isDownloading]);

  const handlePrint = useCallback(async (mode = 'half-a4') => {
    if (isPrinting) return;

    // Set print mode and printing mode immediately
    setPrintMode(mode);
    setPrintingMode(mode);
    setIsPrinting(true);

    try {
      // Only fetch company data if not already loaded and not provided via props
      if (!companyData && !companyDataProp) {
        try {
          const token = localStorage.getItem("companyToken");
          if (token) {
            const response = await api.get("/company/profile", {
              headers: { Authorization: `Bearer ${token}` }
            });
            setCompanyData(response.data.company);
            // Small delay for state update
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (error) {
          console.error("Failed to fetch company before print:", error);
        }
      }

      // Minimal delay before print to allow state update
      setTimeout(() => {
        window.print();
        // Reset loading after print dialog appears
        setTimeout(() => {
          setIsPrinting(false);
          setPrintingMode(null);
        }, 500);
      }, 100);
    } catch (error) {
      console.error("Print error:", error);
      setIsPrinting(false);
      setPrintingMode(null);
    }
  }, [companyData, isPrinting]);

  const isUrdu = language === 'ur';

  // Get default company from receipts or fetched companyData - evaluated at render time
  // This function is called during render to get the latest companyData state
  // Memoize default company to avoid recalculating on every render
  const defaultCompany = useMemo(() => {
    // First check if receipts have company data
    if (receiptsToDisplay.length > 0 && receiptsToDisplay[0]?.company && Object.keys(receiptsToDisplay[0].company).length > 0) {
      return receiptsToDisplay[0].company;
    }
    // Then use fetched companyData
    if (companyData && Object.keys(companyData).length > 0) {
      return companyData;
    }
    return {};
  }, [receiptsToDisplay, companyData]);

  const ReceiptCard = ({ receipt, defaultCompanyProp, companyDataProp }) => {
    // Memoize basic receipt data
    const party = receipt?.party || {};
    const balance = receipt?.balance || 0;
    const latestTransaction = receipt?.latestTransaction || null;
    const previousBalance = receipt?.previousBalance || 0;
    const newBillAmount = receipt?.newBillAmount || 0;
    const amountPaid = receipt?.amountPaid !== undefined && receipt?.amountPaid !== null ? parseFloat(receipt.amountPaid) : 0;

    // Check if this is a customer receipt
    const isCustomerReceipt = receipt?.party?.type === 'customer';

    // Memoize customer daily sales calculations
    const customerDailySales = receipt?.customerDailySales || [];
    const hasDailySales = isCustomerReceipt && customerDailySales.length > 0;

    // Memoize calculated new bill amount
    const calculatedNewBillAmount = useMemo(() => {
      if (!hasDailySales) return newBillAmount;

      const totalFromItems = customerDailySales.reduce((sum, sale) => {
        const saleTotal = sale.items?.reduce((itemSum, item) => {
          return itemSum + (parseFloat(item.lineTotal) || 0);
        }, 0) || 0;
        return sum + saleTotal;
      }, 0);

      return totalFromItems > 0 ? totalFromItems : newBillAmount;
    }, [hasDailySales, customerDailySales, newBillAmount]);

    // Memoize effective new bill amount
    const effectiveNewBillAmount = hasDailySales ? calculatedNewBillAmount : newBillAmount;

    // Memoize total payable calculation
    const totalPayable = useMemo(() => {
      // First try receipt.totalPayable
      if (receipt?.totalPayable !== undefined && receipt?.totalPayable !== null && !isNaN(receipt.totalPayable) && receipt.totalPayable > 0) {
        return Number(receipt.totalPayable);
      }
      // Then try balance
      else if (balance !== undefined && balance !== null && !isNaN(balance)) {
        return Math.abs(Number(balance));
      }
      // Calculate from previousBalance + effectiveNewBillAmount if available
      else if (previousBalance !== undefined && effectiveNewBillAmount !== undefined) {
        return Math.abs(Number(previousBalance)) + Math.abs(Number(effectiveNewBillAmount));
      }
      // Default to 0
      return 0;
    }, [receipt?.totalPayable, balance, previousBalance, effectiveNewBillAmount]);

    // Ensure totalPayable is a valid positive number
    const displayTotalPayable = totalPayable > 0 ? totalPayable : 0;

    // Get company - use receipt's company first, then fallback to defaultCompanyProp, then companyDataProp
    let company = {};
    if (receipt?.company && Object.keys(receipt.company).length > 0) {
      company = receipt.company;
    } else if (defaultCompanyProp && Object.keys(defaultCompanyProp).length > 0) {
      company = defaultCompanyProp;
    } else if (companyDataProp && Object.keys(companyDataProp).length > 0) {
      // Use companyDataProp passed as prop to ensure latest state
      company = companyDataProp;
    }

    // Get bill/sale number - prefer receipt.billNumber (which contains sale number for customers)
    // Ensure it's always a string (handle case where it might be an object)
    let billNumber = receipt?.billNumber;

    // Convert to string if it's not already a string
    if (billNumber && typeof billNumber !== 'string') {
      if (typeof billNumber === 'object' && billNumber !== null) {
        billNumber = billNumber.toString();
      } else {
        billNumber = String(billNumber);
      }
    }

    // If still no billNumber, try to get from transaction
    if (!billNumber || billNumber === 'null' || billNumber === 'undefined') {
      if (latestTransaction) {
        if (isCustomerReceipt && latestTransaction.saleId) {
          billNumber = latestTransaction.saleId.toString().slice(-6);
        } else if (!isCustomerReceipt && latestTransaction.billId) {
          billNumber = latestTransaction.billId.toString().slice(-6);
        } else {
          billNumber = '-';
        }
      } else {
        billNumber = '-';
      }
    }

    // Remove any existing prefix if it's already there (to avoid duplication)
    if (billNumber && billNumber !== '-') {
      billNumber = billNumber.replace(/^(?:سیل|Sale|بل|Bill)\s*#\s*/i, '').trim();
    }

    let billDate = receipt?.billDate ? new Date(receipt.billDate).toLocaleDateString('en-GB') : (latestTransaction?.date ? new Date(latestTransaction.date).toLocaleDateString('en-GB') : '-');

    // Receipt date (current date when receipt is generated)
    let receiptDate = receipt?.date ? new Date(receipt.date).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB');

    // Add prefix based on receipt type (if not already present)
    if (billNumber && billNumber !== '-') {
      if (isCustomerReceipt) {
        billNumber = isUrdu ? `سیل # ${billNumber}` : `Sale # ${billNumber}`;
      } else {
        billNumber = isUrdu ? `بل # ${billNumber}` : `Bill # ${billNumber}`;
      }
    }

    // Get company name with fallbacks - ensure it's always a string
    // Try multiple sources to ensure we get the company name
    // Check receipt company, then prop, then direct access to ensure print view works
    let companyName = '';

    if (isUrdu) {
      companyName = company?.shopNameUrdu ||
        company?.shopName ||
        company?.username ||
        companyDataProp?.shopNameUrdu ||
        companyDataProp?.shopName ||
        companyDataProp?.username ||
        receipt?.company?.shopNameUrdu ||
        receipt?.company?.shopName ||
        receipt?.company?.username ||
        '';
    } else {
      companyName = company?.shopName ||
        company?.username ||
        companyDataProp?.shopName ||
        companyDataProp?.username ||
        receipt?.company?.shopName ||
        receipt?.company?.username ||
        '';
    }

    // If still empty, try to get from localStorage as last resort
    if (!companyName) {
      try {
        const storedCompany = localStorage.getItem("companyData");
        if (storedCompany) {
          const parsed = JSON.parse(storedCompany);
          companyName = isUrdu ? (parsed?.shopNameUrdu || parsed?.shopName || parsed?.username || '') : (parsed?.shopName || parsed?.username || '');
        }
      } catch (e) {
        // Ignore localStorage errors
      }
    }

    // displayTotalPayable is already calculated in useMemo above (line 205)

    return (
      <div className="flex flex-col" style={{ width: '100mm', boxSizing: 'border-box' }}>
        {/* Receipt Card */}
        <div className="bg-white border-2 border-gray-400 rounded-lg p-3 pt-4 flex flex-col shadow-sm" style={{ width: '100mm', minHeight: '140mm', boxSizing: 'border-box', pageBreakInside: 'avoid' }}>
          {/* Header: Company Name, Phone, Address */}
          <div className="border-b-2 border-gray-400 pb-1 mb-1">
            <div className="relative flex flex-col items-center mb-1">
              {/* Logo - Top Right (Absolute) */}
              <div className="absolute right-0 top-0">
                {company?.logo && (
                  <SafeImage
                    src={company.logo}
                    alt="Logo"
                    className="h-16 w-16 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </div>

              {/* Company Info - Centered */}
              <div className="w-full text-center">
                <h1
                  className={`text-2xl font-bold text-brand-header text-center w-full mb-1 ${isUrdu ? 'font-urdu' : ''}`}
                  style={{
                    ...(isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}),
                    color: '#1e40af',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact',
                    visibility: 'visible',
                    opacity: 1
                  }}
                  data-company-name={companyName}
                >
                  {companyName || company?.shopName || company?.username || company?.shopNameUrdu || companyDataProp?.shopName || companyDataProp?.username || ' '}
                </h1>

                {/* Display contacts and persons phone numbers in single row */}
                {(() => {
                  const phoneNumbers = [];

                  // Collect all phone numbers from contacts
                  if (company?.contacts && company.contacts.length > 0) {
                    company.contacts.forEach((contact) => {
                      if (contact.number && contact.number.trim()) {
                        phoneNumbers.push({
                          name: contact.name,
                          number: contact.number
                        });
                      }
                    });
                  }

                  // Collect all phone numbers from persons
                  if (company?.persons && company.persons.length > 0) {
                    company.persons.forEach((person) => {
                      if (person.contactNumber && person.contactNumber.trim()) {
                        phoneNumbers.push({
                          name: person.name,
                          number: person.contactNumber
                        });
                      }
                    });
                  }

                  // Reverse the order: second becomes first, first becomes second
                  const reversedPhoneNumbers = phoneNumbers.reverse();

                  return reversedPhoneNumbers.length > 0 ? (
                    <div className="flex justify-between items-center mt-1 gap-8">
                      {reversedPhoneNumbers.length > 0 && (
                        <span className="text-xs text-gray-700 font-semibold text-left">
                          {reversedPhoneNumbers[0].name ? `${reversedPhoneNumbers[0].name}: ` : ''}{reversedPhoneNumbers[0].number}
                        </span>
                      )}
                      {reversedPhoneNumbers.length > 1 && (
                        <span className="text-xs text-gray-700 font-semibold text-right">
                          {reversedPhoneNumbers[1].name ? `${reversedPhoneNumbers[1].name}: ` : ''}{reversedPhoneNumbers[1].number}
                        </span>
                      )}
                    </div>
                  ) : null;
                })()}

                {/* Display Address below contacts */}
                {company?.address && (
                  <div className="mt-1 text-center">
                    <p className="text-xs text-gray-700" style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                      {company.address}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-start mb-2">
            <div className="text-left">
              <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">{isUrdu ? 'تاریخ' : 'Date'}</p>
              <p className="font-bold text-xs text-gray-800">{receiptDate}</p>
            </div>
            {/* Sale/Bill number hidden as requested */}
            {/* 
            {billNumber && billNumber !== '-' && (
              <div className="text-right">
                <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">{isUrdu ? 'نمبر' : 'Number'}</p>
                <p className={`font-bold text-xs text-gray-800 ${isUrdu ? 'font-urdu' : ''}`}>{billNumber}</p>
              </div>
            )}
            */}
          </div>

          {/* Customer Name - Centered */}
          <div className="text-center mb-2">
            <div className="inline-block border-b-2 border-gray-400 px-8 pb-1">
              <p className="font-bold text-base leading-relaxed" style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                {isUrdu ? (party.nameUrdu || party.name) : party.name}
              </p>
            </div>
          </div>

          {/* Receipt Details */}
          <div className="flex-1">


            {/* Detailed Items Table (Tafseeli Akhrajat) - For Customer Receipts Only - After Previous Balance */}
            {hasDailySales && (
              <div className="mt-1 mb-1">
                <h3 className={`text-xs font-bold mb-1 text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                  {isUrdu ? 'خریداری کی تفصیل' : 'Purchase Details'}
                </h3>
                <div>
                  <table className="w-full border-collapse border-2 border-gray-600 text-[10px]" style={{ fontSize: '0.65rem' }}>
                    <thead>
                      <tr className="bg-gray-100 border-b-2 border-gray-600">
                        <th className={`border border-gray-400 px-1 py-1 text-xs font-bold text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                          {isUrdu ? 'تفصیل' : 'Item Name'}
                        </th>
                        <th className={`border border-gray-400 px-1 py-1 text-xs font-bold text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                          {isUrdu ? 'دانہ' : 'Dana'}
                        </th>
                        <th className={`border border-gray-400 px-1 py-1 text-xs font-bold text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                          {isUrdu ? 'مقدار' : 'Quantity'}
                        </th>
                        <th className={`border border-gray-400 px-1 py-1 text-xs font-bold text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                          {isUrdu ? 'فی یونٹ قیمت' : 'Rate'}
                        </th>
                        <th className={`border border-gray-400 px-1 py-1 text-xs font-bold text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                          {isUrdu ? 'کل رقم' : 'Amount'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerDailySales.flatMap((sale) =>
                        (sale.items || []).map((item, itemIndex) => (
                          <tr key={`${sale._id}-${itemIndex}`}>
                            <td className={`border border-gray-400 px-1 py-1 text-xs text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                              {isUrdu
                                ? (item.descriptionUrdu || item.description || item.nameUrdu || item.name || '-')
                                : (item.description || item.name || '-')}
                            </td>
                            <td className={`border border-gray-400 px-1 py-1 text-xs text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                              {item.dana || '-'}
                            </td>
                            <td className={`border border-gray-400 px-1 py-1 text-xs text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                              {item.quantity || 0} {item.unit ? (itemUnitLabels[item.unit.toLowerCase()] ? (isUrdu ? itemUnitLabels[item.unit.toLowerCase()].ur : item.unit) : item.unit) : ''}
                            </td>
                            <td className={`border border-gray-400 px-1 py-1 text-xs text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                              {(item.ratePerUnit || 0).toLocaleString()}
                            </td>
                            <td className={`border border-gray-400 px-1 py-1 text-xs text-center font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                              {(item.lineTotal || 0).toLocaleString()}
                            </td>
                          </tr>
                        ))
                      )}
                      {/* Subtotal row removed from here */}
                    </tbody>
                  </table>
                </div>
                {/* Total row outside the table box border */}
                <div className="flex justify-between border-t border-gray-300 pt-1 mt-1">
                  <span className={`text-xs font-bold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                    {isUrdu ? 'آج کی کل سیل:' : 'Today\'s Total Sale:'}
                  </span>
                  <span className="text-xs font-bold">Rs. {calculatedNewBillAmount.toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* Previous Balance */}
            <div className="flex justify-between border-b border-gray-200 pb-1 mb-1">
              <span className="text-xs font-medium">{isUrdu ? 'سابقہ رقم:' : 'Previous Balance:'}</span>
              <span className="text-xs font-semibold">Rs. {previousBalance.toLocaleString()}</span>
            </div>

            {/* Today's Sale/Bill hidden as it's already in the table above */}
            {/* 
            <div className="flex justify-between border-b border-gray-200 pb-1 mb-1">
              <span className="text-xs font-medium text-blue-600">
                {isCustomerReceipt
                  ? (isUrdu ? 'آج کی سیل:' : 'Today\'s Sale:')
                  : (isUrdu ? 'آج کا بل:' : 'Today\'s Bill:')}
              </span>
              <span className="text-xs font-semibold text-blue-600">Rs. {calculatedNewBillAmount.toLocaleString()}</span>
            </div>
            */}

            {/* Paid amount hidden as requested */}
            {/* 
            {amountPaid > 0 && (
              <div className="flex justify-between border-b border-gray-200 pb-1 mb-1">
                <span className="text-xs font-medium">{isUrdu ? 'وصولی / ادائیگی:' : 'Paid/Received:'}</span>
                <span className="text-xs font-semibold">Rs. {amountPaid.toLocaleString()}</span>
              </div>
            )}
            */}

            <div className="flex justify-between border-t-2 border-gray-400 pt-1 mt-1">
              <span className="text-sm font-bold">{isUrdu ? 'کل قابل وصول:' : 'Total Payable:'}</span>
              <span
                className="text-sm font-bold text-brand-header"
                data-total-payable={displayTotalPayable}
                style={{
                  color: '#1e40af',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact'
                }}
              >
                Rs. {displayTotalPayable.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>

          {/* Footer Area */}
          <div className="border-t-2 border-dashed border-gray-300 pt-2 mt-auto">
            <div className="flex justify-between items-end text-[10px] text-gray-400 font-medium italic">
              <div className="flex flex-col">
                {/* <span className="opacity-70">Powered by Bexon</span> */}
              </div>
              <div className="flex flex-col items-center">
                <div className="w-24 border-b border-gray-400 mb-1"></div>
                <span className={`text-[10px] ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                  {isUrdu ? 'دستخط' : 'Signature'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>{isUrdu ? 'رسید' : 'Receipt'} - Bexon</title>
      </Helmet>

      <div className={`${isInline ? '' : 'min-h-screen bg-gray-100 p-6'} print:p-0 print:bg-white ${isUrdu ? 'font-urdu' : ''}`} dir={isUrdu ? 'rtl' : 'ltr'}>
        {/* Action Buttons - Hidden in inline mode */}
        {!isInline && (
          <div className="max-w-4xl mx-auto mb-6 print:hidden flex justify-between">
            <button
              onClick={onClose}
              disabled={isPrinting || isDownloading}
              className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUrdu ? 'واپس' : 'Back'}
            </button>
            <div className="flex gap-2">
              <button
                onClick={() => handlePrint('half-a4')}
                disabled={isPrinting || isDownloading}
                className="bg-brand-header text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {printingMode === 'half-a4' && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {printingMode === 'half-a4' ? (isUrdu ? 'پرنٹ ہو رہا ہے...' : 'Printing...') : (isUrdu ? 'پرنٹ' : 'Print')}
              </button>
              <button
                onClick={() => handlePrint('full-a4')}
                disabled={isPrinting || isDownloading}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {printingMode === 'full-a4' && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {printingMode === 'full-a4' ? (isUrdu ? 'پرنٹ ہو رہا ہے...' : 'Printing...') : (isUrdu ? '4 پرنٹس' : '4 Prints')}
              </button>
              <button
                onClick={handleDownloadPDF}
                disabled={isPrinting || isDownloading}
                className="bg-red-600 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloading && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isDownloading ? (isUrdu ? 'ڈاؤن لوڈ ہو رہا ہے...' : 'Downloading...') : (isUrdu ? 'PDF ڈاؤن لوڈ' : 'Download PDF')}
              </button>
            </div>
          </div>
        )}

        {/* Receipt Pages */}
        <div ref={receiptRef} className={isInline ? "flex flex-wrap gap-4 justify-center" : ""}>
          {receiptPages.map((pageReceipts, pageIdx) => {
            // Check print mode: full-a4 = 2x2 grid, half-a4 = single column
            const isSingleColumn = printMode === 'half-a4';
            const isFullA4 = printMode === 'full-a4';
            return (
              <div
                key={pageIdx}
                className={`${pageIdx > 0 ? 'print:break-before-page' : ''}`}
                data-single-column={isSingleColumn}
                data-print-mode={printMode}
                style={{
                  width: isSingleColumn ? "105mm" : "210mm",
                  // Use auto height for single column to prevent empty pages
                  height: isFullA4 ? "296mm" : "auto",
                  minHeight: isFullA4 ? "296mm" : "auto",
                  maxHeight: isFullA4 ? "296mm" : "none",
                  margin: "0 auto",
                  padding: isSingleColumn ? "2mm" : "2mm",
                  boxSizing: "border-box",
                  display: isFullA4 ? "grid" : "flex",
                  flexDirection: isSingleColumn ? "column" : undefined,
                  justifyContent: "flex-start",
                  gridTemplateColumns: isFullA4 ? "repeat(2, 102mm)" : undefined,
                  gridTemplateRows: isFullA4 ? "repeat(2, 145mm)" : undefined,
                  gap: isFullA4 ? "2mm" : "2mm"
                }}
              >
                {pageReceipts.map((receipt, idx) => {
                  // Get fresh company data on each render
                  return (
                    <div
                      key={receipt.party._id || idx}
                      className="w-full"
                      style={{
                        width: '100mm',
                        height: isFullA4 ? '142mm' : '140mm',
                        minHeight: isFullA4 ? '142mm' : '140mm',
                        maxHeight: isFullA4 ? '142mm' : '140mm',
                        boxSizing: 'border-box',
                        marginBottom: isFullA4 ? '0' : '4mm'
                      }}
                    >
                      <ReceiptCard receipt={receipt} defaultCompanyProp={defaultCompany} companyDataProp={companyData} />
                    </div>
                  );
                })}

                {/* Empty slots to fill grid for full A4 mode (4 receipts per page) */}
                {isFullA4 && pageReceipts.length < 4 && pageIdx === receiptPages.length - 1 && Array.from({ length: 4 - pageReceipts.length }).map((_, idx) => (
                  <div
                    key={`empty-${idx}`}
                    className="w-full"
                    style={{
                      width: '100mm',
                      height: '142mm',
                      minHeight: '142mm',
                      maxHeight: '142mm',
                      boxSizing: 'border-box'
                    }}
                  ></div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @media print {
          /* Hide buttons and non-receipt content */
          button { 
            display: none !important; 
          }
          
          /* Page setup - A4 portrait for all modes */
          @page { 
            size: A4 portrait;
            margin: 0; /* Changed from 3mm to 0 to prevent overflow */
          }
          
          html, body { 
            margin: 0 !important; 
            padding: 0 !important;
            background: white !important; 
            overflow: visible !important;
            height: 100%;
          }
          
          /* Force ALL divs with data-print-mode to respect their settings */
          [data-print-mode] {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Force grid display for full-a4 mode - HIGHEST PRIORITY */
          div[data-print-mode="full-a4"],
          [data-print-mode="full-a4"] {
            display: grid !important;
            column-gap: 5mm !important;
            row-gap: 5mm !important;
            width: 210mm !important;
            height: 296mm !important;
            max-width: 210mm !important;
            max-height: 296mm !important;
            margin: 0 auto !important;
            padding: 2.5mm !important;
            box-sizing: border-box !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            page-break-after: always !important;
            break-after: page !important;
            grid-template-columns: 100mm 100mm !important;
            grid-template-rows: 143mm 143mm !important;
            overflow: hidden !important;
          }
          
          div[data-print-mode="full-a4"]:last-of-type,
          [data-print-mode="full-a4"]:last-of-type {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          
          /* Ensure children of full-a4 mode stay in grid */
          [data-print-mode="full-a4"] > div {
            grid-column: auto !important;
            grid-row: auto !important;
          }
          
          /* Receipt container - Dynamic width based on mode */
          div[ref] {
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }
          
          /* Page breaks - Each page on new page */
          .print\\:break-before-page {
            page-break-before: always !important;
            break-before: page !important;
          }
          
          /* Half A4 mode: Single column page break behavior - ONLY for half-a4 */
          div[data-single-column="true"]:not([data-print-mode="full-a4"]),
          div[data-print-mode="half-a4"] {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            page-break-after: always !important;
            break-after: page !important;
            width: 105mm !important;
            max-width: 105mm !important;
            display: flex !important;
            flex-direction: column !important;
          }
          
          /* Full A4 mode: Grid layout page break behavior - REMOVED (defined above) */
          
          /* Last page should not break */
          div[data-single-column="true"]:last-child,
          div[data-print-mode="full-a4"]:last-child {
            page-break-after: avoid !important;
            break-after: avoid !important;
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          
          /* Hide Dashboard Header and Navigation */
          header,
          [class*="header"],
          [class*="Header"],
          nav,
          [class*="navigation"],
          [class*="Navigation"],
          [class*="sticky"],
          [style*="sticky"],
          [style*="position: sticky"],
          [style*="position:sticky"] {
            display: none !important;
          }
          
          /* Hide any element with header-like classes */
          div[class*="DashboardHeader"],
          div[class*="dashboard-header"],
          div[class*="header"] {
            display: none !important;
          }
          
          /* Main container */
          .min-h-screen,
          .min-h-screen * {
            min-height: auto !important;
            padding: 0 !important;
            margin: 0 !important;
            background: white !important;
            background-color: white !important;
            background-image: none !important;
          }
          
          /* Grid layout - Single column for half A4 ONLY (exclude full-a4) */
          div[data-print-mode="half-a4"]:not([data-print-mode="full-a4"]),
          div[data-single-column="true"]:not([data-print-mode="full-a4"]) {
            display: flex !important;
            flex-direction: column !important;
            gap: 4mm !important;
            padding: 0 !important;
            margin: 0 auto !important;
            width: 105mm !important;
            min-height: 297mm !important;
            max-width: 105mm !important;
            box-sizing: border-box !important;
            overflow: visible !important;
          }
          
          /* Each receipt wrapper - Half A4 mode ONLY (exclude full-a4) */
          div[data-print-mode="half-a4"]:not([data-print-mode="full-a4"]) > div,
          div[data-single-column="true"]:not([data-print-mode="full-a4"]) > div {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin: 0 auto 4mm auto !important;
            padding: 0 !important;
            width: 100mm !important;
            height: auto !important;
            max-width: 100mm !important;
            display: block !important;
            visibility: visible !important;
          }
          
          /* Each receipt wrapper - Full A4 mode (2x2) - CRITICAL FOR GRID */
          [data-print-mode="full-a4"] > div,
          div[data-print-mode="full-a4"] > div {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100mm !important;
            height: 143mm !important;
            max-width: 100mm !important;
            max-height: 143mm !important;
            display: block !important;
            visibility: visible !important;
            overflow: hidden !important;
            /* Grid items must NOT float or be positioned */
            float: none !important;
            position: relative !important;
          }
          
          /* Individual receipt styling - Half A4 mode */
          div[data-print-mode="half-a4"] .bg-white.border-2,
          div[data-single-column="true"] .bg-white.border-2 {
            width: 100mm !important;
            height: 140mm !important;
            min-width: 100mm !important;
            max-width: 100mm !important;
            min-height: 140mm !important;
            max-height: 140mm !important;
            box-sizing: border-box !important;
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            padding: 5mm 1mm 3mm 1mm !important;
            border: 2px solid #9ca3af !important;
            border-radius: 8px !important;
            margin: 0 auto !important;
            overflow: visible !important;
          }
          
          /* Individual receipt styling - Full A4 mode (4 receipts) */
          [data-print-mode="full-a4"] .bg-white.border-2,
          div[data-print-mode="full-a4"] .bg-white.border-2 {
            width: 100mm !important;
            height: 140mm !important;
            min-width: 100mm !important;
            max-width: 100mm !important;
            min-height: 140mm !important;
            max-height: 140mm !important;
            box-sizing: border-box !important;
            display: flex !important;
            flex-direction: column !important;
            visibility: visible !important;
            opacity: 1 !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            padding: 5mm 3mm 3mm 3mm !important;
            border: 2px solid #9ca3af !important;
            border-radius: 8px !important;
            margin: auto !important;
            overflow: hidden !important;
            background: white !important;
            box-shadow: none !important;
          }

          /* Remove border from any wrapper divs to prevent double border */
          [data-print-mode="full-a4"] > div > div {
            border: none !important;
            background: transparent !important;
            padding: 0 !important;
            margin: 0 !important;
            width: 100% !important;
            height: 100% !important;
          }
          
          /* Adjust font sizes for full A4 mode to fit content */
          div[data-print-mode="full-a4"] .text-2xl {
            font-size: 1.1rem !important;
          }
          
          div[data-print-mode="full-a4"] .text-xl {
            font-size: 0.9rem !important;
          }
          
          div[data-print-mode="full-a4"] .text-xs {
            font-size: 0.65rem !important;
          }
          
          div[data-print-mode="full-a4"] .text-sm {
            font-size: 0.75rem !important;
          }
          
          /* CRITICAL: Override any flex/column display for full-a4 container */
          *[data-print-mode="full-a4"] {
            display: grid !important;
          }
          
          /* Force all 4 children to be visible in full-a4 mode */
          [data-print-mode="full-a4"] > div:nth-child(1) { grid-column: 1; grid-row: 1; }
          [data-print-mode="full-a4"] > div:nth-child(2) { grid-column: 2; grid-row: 1; }
          [data-print-mode="full-a4"] > div:nth-child(3) { grid-column: 1; grid-row: 2; }
          [data-print-mode="full-a4"] > div:nth-child(4) { grid-column: 2; grid-row: 2; }
          
          /* Header section spacing - Company name, contacts, customer info */
          .border-b-2.pb-3,
          div.border-b-2 {
            padding-bottom: 2mm !important;
            margin-bottom: 2mm !important;
          }
          
          /* Header rows - Company info centering */
          .relative.flex.flex-col.items-center {
            margin-bottom: 2mm !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            text-align: center !important;
          }
          
          /* Contact numbers row */
          .flex.justify-between.text-xs.text-gray-600 {
            margin-top: 1.5mm !important;
            margin-bottom: 1.5mm !important;
            line-height: 1.5 !important;
          }
          
          /* Customer and date row */
          .grid.grid-cols-2.gap-2.mt-2 {
            margin-top: 2mm !important;
            gap: 2mm !important;
          }
          
          /* Previous balance section */
          .mt-3 {
            margin-top: 2.5mm !important;
          }
          
          /* Table headers row spacing */
          .grid.grid-cols-6.gap-1.bg-gray-100.px-2.py-1 {
            padding-top: 1.5mm !important;
            padding-bottom: 1.5mm !important;
            margin-bottom: 1mm !important;
          }
          
          /* Table rows spacing */
          table tr {
            line-height: 1.6 !important;
          }
          
          table td,
          table th {
            padding-top: 2mm !important;
            padding-bottom: 2mm !important;
          }
          
          /* Grid items (Products/Items rows) spacing */
          .grid-cols-6 > div {
            padding-top: 1.5mm !important;
            padding-bottom: 1.5mm !important;
          }
          
          /* Items section spacing */
          div[class*="grid-cols"] {
            gap: 1.5mm !important;
            row-gap: 2.5mm !important;
          }
          
          /* Each item row */
          .grid.grid-cols-6.gap-1.text-xs.py-1 {
            padding-top: 1.5mm !important;
            padding-bottom: 1.5mm !important;
            margin-bottom: 1mm !important;
          }
          
          /* Footer section spacing - Totals */
          .border-t-2.pt-2.mt-3 {
            padding-top: 3mm !important;
            margin-top: 3mm !important;
          }
          
          /* Total rows spacing */
          .flex.justify-between.mb-1,
          .flex.justify-between.mb-2 {
            margin-bottom: 3mm !important;
            line-height: 1.6 !important;
            padding-bottom: 1mm !important;
          }
          
          /* Space between receipt sections */
          .space-y-3 > * + * {
            margin-top: 3mm !important;
          }
          
          /* Remove ALL background colors */
          * {
            background: white !important;
            background-color: white !important;
            background-image: none !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Ensure Total Payable value is visible in print */
          span[data-total-payable],
          span.text-brand-header {
            visibility: visible !important;
            opacity: 1 !important;
            color: #1e40af !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            display: inline !important;
          }
          
          /* Ensure company name is visible in print */
          h1[data-company-name],
          h1.text-brand-header {
            visibility: visible !important;
            opacity: 1 !important;
            color: #1e40af !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            display: block !important;
          }
          
          /* Ensure all numeric values are visible */
          span.font-bold,
          span.font-semibold {
            visibility: visible !important;
            opacity: 1 !important;
            display: inline !important;
          }
          
          /* Ensure all text content is visible */
          p, span, td, th, h1, h2, h3, div {
            visibility: visible !important;
            opacity: 1 !important;
          }
          
          /* Make sure receipts stack vertically */
          .flex-col {
            display: flex !important;
            flex-direction: column !important;
          }
        }
      `}</style>
    </>
  );
}

export default ReceiptView;

